import { create } from 'zustand';

/**
 * Action button for toast notifications.
 */
export interface ToastAction {
  /** Button label */
  label: string;
  /** Callback function when button is clicked */
  handler: () => void;
  /** Button style variant */
  variant?: 'primary' | 'secondary' | 'danger';
}

/**
 * Toast message with enhanced features for resilience system.
 */
export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning' | 'retrying' | 'offline' | 'connection-restored';
  duration?: number;
  /** Action buttons (e.g., "Retry Now", "View Status") */
  actions?: ToastAction[];
  /** Whether toast can be dismissed by user */
  dismissible?: boolean;
  /** Progress percentage for retrying toasts (0-100) */
  progress?: number;
  /** Additional metadata */
  metadata?: Record<string, any>;
}

interface ToastState {
  toasts: ToastMessage[];

  showToast: (
    message: string,
    type?: ToastMessage['type'],
    duration?: number,
    options?: {
      actions?: ToastAction[];
      dismissible?: boolean;
      progress?: number;
      metadata?: Record<string, any>;
    }
  ) => string;
  updateToast: (id: string, updates: Partial<ToastMessage>) => void;
  showRetryToast: (
    attempt: number,
    maxAttempts: number,
    delayMs: number,
    operationType?: string
  ) => string;
  showOfflineToast: (message?: string) => string;
  showConnectionRestoredToast: () => string;
  showErrorWithRetry: (
    message: string,
    onRetry: () => void,
    onViewDetails?: () => void
  ) => string;
  showServiceOutageToast: (message: string, onViewStatus: () => void) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

/** Mirrors Angular Toast.generateId(). */
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],

  showToast: (message, type = 'info', duration, options) => {
    const id = generateId();
    const toast: ToastMessage = {
      id,
      message,
      type,
      duration,
      actions: options?.actions,
      dismissible: options?.dismissible ?? true,
      progress: options?.progress,
      metadata: options?.metadata,
    };

    set((state) => ({ toasts: [...state.toasts, toast] }));

    // Auto-remove toast if duration is specified
    if (duration && duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }

    return id; // Return ID so caller can update or remove it
  },

  updateToast: (id, updates) => {
    set((state) => ({
      toasts: state.toasts.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      ),
    }));
  },

  showRetryToast: (attempt, maxAttempts, delayMs, operationType) => {
    const delaySeconds = Math.ceil(delayMs / 1000);
    const operation = operationType ? ` ${operationType}` : '';
    const message = `Retrying${operation} in ${delaySeconds}s... (${attempt}/${maxAttempts})`;

    return get().showToast(message, 'retrying', delayMs, {
      dismissible: false,
      progress: Math.round((attempt / maxAttempts) * 100),
    });
  },

  showOfflineToast: (message) => {
    return get().showToast(
      message || 'You are offline. Some features may be limited.',
      'offline',
      0, // Don't auto-dismiss
      {
        dismissible: true,
      }
    );
  },

  showConnectionRestoredToast: () => {
    return get().showToast('Connection restored', 'connection-restored', 3000, {
      dismissible: true,
    });
  },

  showErrorWithRetry: (message, onRetry, onViewDetails) => {
    const actions: ToastAction[] = [
      {
        label: 'Retry Now',
        handler: onRetry,
        variant: 'primary',
      },
    ];

    if (onViewDetails) {
      actions.push({
        label: 'View Details',
        handler: onViewDetails,
        variant: 'secondary',
      });
    }

    return get().showToast(message, 'error', 0, {
      actions,
      dismissible: true,
    });
  },

  showServiceOutageToast: (message, onViewStatus) => {
    return get().showToast(message, 'error', 0, {
      actions: [
        {
          label: 'View Status',
          handler: onViewStatus,
          variant: 'primary',
        },
      ],
      dismissible: false,
    });
  },

  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((toast) => toast.id !== id) }));
  },

  clearAllToasts: () => {
    set({ toasts: [] });
  },
}));
